import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  TextField
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StoreIcon from '@mui/icons-material/Store';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import outletService from '../services/outletService';
import { getSalesReportData,downloadSalesReportPDF } from '../services/reportService';
import { useRestaurant } from '../contexts/RestaurantContext';
import { useNotification } from '../contexts/NotificationContext';

const SalesReportManagement = () => {

  const { outlets } = useRestaurant()
  const { showSuccess, showError } = useNotification()

  // State for time filter
  const [timeframe, setTimeframe] = useState('all');
  const [outletSelection, setOutletSelection] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [salesData, setSalesData] = useState({})
  const [orderSales, setOrderSales] = useState({})
  const [adminOutlets,setAdminOutlets] = useState([])
  const [paymentSales, setPaymentSales] = useState({})
  const [categorySales, setCategorySales] = useState({})
  const [outletSales, setOutletSales] = useState([])
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [superAdmin,setSuperAdmin] = useState(true)

  // Time filter options
  const timeframeOptions = [
    { name: 'All Time', value: 'all' },
    { name: 'Today', value: "today" },
    { name: 'Yesterday', value: 'yesterday' },
    { name: 'Last 7 Days', value: 'last_7_days' },
    { name: 'Custom', value: 'custom' }];


   useEffect( () => {
      async function fetchAdminOutlets (){
        try{
          const response = await outletService.getOutlets()
          setAdminOutlets(response)
        }
        catch(error){
          console.error(error)
        }
      }
      fetchAdminOutlets()

      let admin_check = JSON.parse(localStorage.getItem('admin_outlet_id'))

      if (admin_check){
        setOutletSelection(admin_check)
      }
  
    },[])

  useEffect(() => {
     let admin_check = JSON.parse(localStorage.getItem('admin_outlet_id'))
    async function getSalesData() {
      try {
        let response = ""
        if (admin_check){
          response = await getSalesReportData(admin_check);
        }
        else{
          response = await getSalesReportData();
        }
        
        // console.log("Report Data => ", response)
        setOrderSales(response?.order_sales)
        setPaymentSales(response?.payment_sales)
        setCategorySales(response?.orders_type_sales)
        setOutletSales(response?.outlet_wise)
      }
      catch (error) {
        showError(error?.message)
      }
    }

    getSalesData()
  }, [])

  const dataChange = async (outlet = outletSelection, time = timeframe) => {
    // console.log("Time Data => ", timeframe)

    // If timeframe is 'custom', we need to pass start_date and end_date
    if (time === 'custom') {
      let dateFilterParam = time;
      // Format dates as YYYY-MM-DD
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      dateFilterParam = `custom:${startDateStr}:${endDateStr}`;

      try {
        const response = await getSalesReportData(outlet, dateFilterParam);
        setOrderSales(response?.order_sales)
        setPaymentSales(response?.payment_sales)
        setCategorySales(response?.orders_type_sales)
        setOutletSales(response?.outlet_wise)
      }
      catch (error) {
        showError(error?.message)
      }
    } else {
      try {
        const response = await getSalesReportData(outlet, time);
        // console.log("Response => ", response)
        setOrderSales(response?.order_sales)
        setPaymentSales(response?.payment_sales)
        setCategorySales(response?.orders_type_sales)
        setOutletSales(response?.outlet_wise)
      }
      catch (error) {
        showError(error?.message)
      }
    }
  }

  // Payment Method Data with specific font colors
  const paymentMethods = [
    {
      name: 'PayNow',
      orders: paymentSales?.paynow?.count,
      amount: paymentSales?.paynow?.sales,
      titleColor: '#1976d2',
      valueColor: '#1976d2'
    },
    {
      name: 'Visa / Mastercard',
      orders: paymentSales?.visa_mastercard?.count,
      amount: paymentSales?.visa_mastercard?.sales,
      titleColor: '#00a650',
      valueColor: '#00a650'
    },
     {
      name: 'Nets',
      orders: paymentSales?.nets?.count,
      amount: paymentSales?.nets?.sales,
      titleColor: '#a3ee',
      valueColor: '#a3ee'
    },
    {
      name: 'Card',
      orders: paymentSales?.card?.count,
      amount: paymentSales?.card?.sales,
      titleColor: '#ff9800',
      valueColor: '#ff9800'
    },
    // {
    //   name: 'UPI',
    //   orders: paymentSales?.upi?.count,
    //   amount: paymentSales?.upi?.sales,
    //   titleColor: '#9c27b0',
    //   valueColor: '#9c27b0'
    // },
    {
      name: 'Cash (Dine In)',
      orders: paymentSales?.cash?.count,
      amount: paymentSales?.cash?.sales,
      titleColor: '#f44336',
      valueColor: '#f44336'
    }
  ];

  const categories = [
    {
      name: 'Takeaway',
      orders: categorySales?.takeaway,
      titleColor: '#2196f3',
      valueColor: '#2196f3'
    },
    {
      name: 'Delivery',
      orders: categorySales?.delivery,
      titleColor: '#4caf50',
      valueColor: '#4caf50'
    },
    {
      name: 'Dine In',
      orders: categorySales?.dineIn,
      titleColor: '#ff9800',
      valueColor: '#ff9800'
    },
    {
      name: 'Catering',
      orders: categorySales?.catering,
      titleColor: '#9c27b0',
      valueColor: '#9c27b0'
    }
  ];

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount);
  };

  // Handle download report
  const handleDownloadReport = async(outlet,time) => {
  
    setIsLoading(true);
    if (time === 'custom') {
      let dateFilterParam = time;
      // Format dates as YYYY-MM-DD
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      dateFilterParam = `custom:${startDateStr}:${endDateStr}`;

      try {
        const response = await downloadSalesReportPDF(outlet, dateFilterParam);
      }
      catch (error) {
        // console.log("Error => ", error)
        showError(error)
      }
    } else {
      try {
        const response = await downloadSalesReportPDF(outlet, time);
      }
      catch (error) {
        // console.log("Error => ", error)
        if(error === 404 ){
          showError("No Data is Found in the Selected Filters , unable to Download Report ")
        }
        else if (error === 500) {
          showError("Something Went Wrong")
        }
        else{
          showError("Something Went Wrong")
        }
        
      }
    }

    setIsLoading(false)
  };

  // Handle custom date change
  const handleCustomDateChange = () => {
    dataChange(outletSelection, 'custom');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{
        p: 3,
        backgroundColor: '#f5f5f5',
        minHeight: '100vh'
      }}>
        {/* Header with Title and Download Button */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#333' }}>
            Sales Report
          </Typography>
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={()=> handleDownloadReport(outletSelection,timeframe)}
            disabled={isLoading}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              },
              px: 3,
              py: 1,
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            {isLoading ? 'Processing...' : 'Download Report'}
          </Button>
        </Box>

        {/* Time Filter Section */}
        <Card sx={{
          mb: 3,
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          backgroundColor: 'white',
          border: '1px solid #e0e0e0'
        }}>
          <CardContent sx={{ p: 2.5 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Timeframe buttons row */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#555', mb: { xs: 1, sm: 0 }, whiteSpace: 'nowrap' }}>
                      TIMEFRAME
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {timeframeOptions.map((option) => (
                        <Button
                          key={option.value}
                          variant={timeframe === option.value ? "contained" : "outlined"}
                          onClick={() => {
                            setTimeframe(option.value);
                            if (option.value !== 'custom') {
                              dataChange(outletSelection, option.value);
                            }
                          }}
                          size="small"
                          sx={{
                            textTransform: 'none',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            backgroundColor: timeframe === option.value ? '#1976d2' : 'transparent',
                            color: timeframe === option.value ? 'white' : '#555',
                            borderColor: timeframe === option.value ? '#1976d2' : '#d0d0d0',
                            '&:hover': {
                              borderColor: timeframe === option.value ? '#1565c0' : '#b0b0b0',
                              backgroundColor: timeframe === option.value ? '#1565c0' : '#f8f8f8'
                            },
                            minWidth: 'auto',
                            px: 1.5,
                            py: 0.5,
                            height: '32px'
                          }}
                        >
                          {option.name}
                        </Button>
                      ))}
                    </Stack>
                  </Box>

                  {/* Date pickers row - only shown when custom is selected */}
                  {timeframe === 'custom' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DatePicker
                        value={startDate}
                        onChange={(newDate) => setStartDate(newDate)}
                        inputFormat="dd/MM/yyyy"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{
                              width: '140px',
                              '& .MuiInputBase-root': {
                                height: '32px',
                                fontSize: '0.75rem'
                              },
                              '& .MuiInputBase-input': {
                                padding: '6px 8px',
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                        )}
                      />

                      <Typography variant="body2" sx={{ color: '#555', whiteSpace: 'nowrap', px: 0.5 }}>
                        to
                      </Typography>

                      <DatePicker
                        value={endDate}
                        onChange={(newDate) => setEndDate(newDate)}
                        inputFormat="dd/MM/yyyy"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{
                              width: '140px',
                              '& .MuiInputBase-root': {
                                height: '32px',
                                fontSize: '0.75rem'
                              },
                              '& .MuiInputBase-input': {
                                padding: '6px 8px',
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                        )}
                      />

                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleCustomDateChange}
                        sx={{
                          height: '32px',
                          minWidth: '60px',
                          fontSize: '0.75rem',
                          backgroundColor: '#1976d2',
                          '&:hover': {
                            backgroundColor: '#1565c0'
                          },
                          ml: 1
                        }}
                      >
                        Apply
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Outlet selection - always on the right */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#555', mb: 1, fontSize: '0.75rem' }}>
                      OUTLET SELECTION
                    </Typography>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={outletSelection}
                        disabled={JSON.parse(localStorage.getItem('admin_outlet_id')) ? true : false}
                        onChange={(e) => {
                          setOutletSelection(e.target.value)
                          dataChange(e.target.value, timeframe)
                        }}
                        sx={{
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          borderColor: '#d0d0d0',
                          height: '32px',
                          '& .MuiSelect-select': {
                            padding: '6px 8px'
                          }
                        }}
                      >
                        <MenuItem key={"all"} value={"all"}>
                          All
                        </MenuItem>
                        {adminOutlets.map((outlet) => (
                          <MenuItem key={outlet.id} value={outlet.id}>
                            {outlet.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Rest of your component remains exactly the same */}
        {/* Sales Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { title: 'Total Orders', value: orderSales.total_orders, titleColor: '#555', valueColor: '#1976d2' },
            { title: 'Gross Sales (SGD)', value: "S$ " + orderSales.gross_sales, titleColor: '#555', valueColor: '#2e7d32' },
            { title: 'Net Sales (SGD)', value: "S$ " + orderSales.net_sales, titleColor: '#555', valueColor: '#0288d1' },
            { title: 'GST Collected', value: "S$ " + orderSales.gst_collected, titleColor: '#555', valueColor: '#ed6c02' },
            { title: 'Avg. Order Value', value: "S$ " + orderSales.avg_sales, titleColor: '#555', valueColor: '#9c27b0' }
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={2.4} key={index}>
              <Card sx={{
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                backgroundColor: 'white',
                height: '100%',
                border: '1px solid #e0e0e0',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                }
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="body2" sx={{ color: item.titleColor, mb: 1.5, fontWeight: 500 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: item.valueColor }}>
                    {item.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Divider */}
        <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

        {/* Outlet-Wise Sales Table */}
        <Card sx={{
          mb: 3,
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          backgroundColor: 'white',
          border: '1px solid #e0e0e0'
        }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#333', mb: 3, fontSize: '1.1rem' }}>
              Outlet-Wise Sales
            </Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #e0e0e0' }}>Outlet Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #e0e0e0' }}>Total Orders</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #e0e0e0' }}>Gross Sales (SGD)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #e0e0e0' }}>Net Sales (SGD)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #e0e0e0' }}>GST Collected (SGD)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outletSales.map((outlet, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>{outlet.name}</TableCell>
                      <TableCell align="right" sx={{ color: '#555' }}>{outlet.total_orders}</TableCell>
                      <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 500 }}>S${outlet.gross_sales?.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>S${outlet.net_sales?.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ color: '#0288d1', fontWeight: 500 }}>S${outlet.gst_collected?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Sales by Payment Method - Full Width */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#333', mb: 2, fontSize: '1.1rem' }}>
            Sales by Payment Method
          </Typography>
          <Grid container spacing={2}>
            {paymentMethods.map((method, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                <Card sx={{
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  backgroundColor: 'white',
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                  }
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: method.titleColor, mb: 1 }}>
                      {method.name}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                          Orders
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#333' }}>
                          {method.orders}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                          Amount
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: method.valueColor }}>
                          {"S" + formatCurrency(method.amount)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Sales by Category - Full Width */}
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#333', mb: 2, fontSize: '1.1rem' }}>
            Sales by Category
          </Typography>
          <Grid container spacing={2}>
            {categories.map((category, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  backgroundColor: 'white',
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                  }
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: category.titleColor, mb: 1 }}>
                      {category.name}
                    </Typography>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Orders
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" sx={{ color: category.valueColor }}>
                        {category.orders}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Footer */}
        {/* <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" sx={{ color: '#777', textAlign: 'center' }}>
            Report generated on {new Date().toLocaleDateString('en-SG', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })} • Timeframe: {timeframe} • Outlet: {outletSelection}
          </Typography>
        </Box> */}
      </Box>
    </LocalizationProvider>
  );
};

export default SalesReportManagement;